import { useMemo, useState } from 'react';
import type { LANG } from '../../constants/languages';
import { toPlainQuote, toQuotePreview } from '../messages/doc-answer';
import { useChunks } from './chunks.hooks';

export interface CitationQuoteView {
  id: string;
  /** Quote as one flat plain-text line. */
  text: string;
  /** Word-boundary preview, or null when `text` fits untruncated. */
  preview: string | null;
}

/**
 * Citations view-model for an answer card: resolves the cited chunk ids (only
 * ids that resolve to real content are counted and shown, so a hallucinated id
 * never inflates the count), plain-text quotes with their previews, the
 * "View Citations (N)" label, and the expand/collapse state.
 */
export function useCitations(chunkIds: string[], lang: LANG['AI_CHAT'], previewLength: number) {
  const [expanded, setExpanded] = useState(false);
  const { chunks } = useChunks(chunkIds);

  const quotes = useMemo(
    () =>
      chunks
        .filter(chunk => chunk.content)
        .map((chunk): CitationQuoteView => {
          const text = toPlainQuote(chunk.content);
          return { id: chunk.id, text, preview: toQuotePreview(text, previewLength) };
        }),
    [chunks, previewLength],
  );

  const label = (lang.PANDA_CITATIONS_COUNT || 'View Citations ({count})').replace(
    '{count}',
    String(quotes.length),
  );

  const toggleExpanded = () => setExpanded(value => !value);

  return { quotes, label, expanded, toggleExpanded };
}
